import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Popover } from 'primeng/popover';
import { Tooltip } from 'primeng/tooltip';
import { AccountService } from '../../../services/account.service';
import { AuthService } from '../../../services/auth.service';
import { LogService } from '../../../services/log.service';
import { ThemeService } from '../../../services/theme.service';
import { WebsocketService } from '../../../services/websocket.service';
import { AccountDto, AccountRole, SerializedAccountDto } from '../../../types/dtos/account.dto';
import { NavigationDto } from '../../../types/dtos/navigation.dto';
import { LoggingActionEnum, LoggingTargetEnum } from '../../../types/enums/logging.enum';
import { TranslateSafePipe } from '../../pipes/translation.pipe';
import { ButtonIconComponent } from '../button-icon/button-icon.component';
import { DialogBase } from '../dialogs/dialog-base/dialog-base';
import { DialogEditUser } from '../dialogs/dialog-edit-user/dialog-edit-user';

@Component({
  selector: 'app-navigation',
  imports: [
    RouterLink,
    ButtonIconComponent,
    RouterLinkActive,
    Tooltip,
    Popover,
    DialogEditUser,
    DialogBase,
    TranslatePipe,
    TranslateSafePipe,
  ],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.css',
})

export class NavigationComponent implements OnInit {
  @ViewChild('dialogEditUser')
  public editAccountDialog!: DialogEditUser;

  public theme!: 'light' | 'dark';
  public isChildMenuOpen: boolean = false;
  public isEditDialogOpen: boolean = false;
  public currentAccount: AccountDto | SerializedAccountDto | null = null;
  public accountRole = AccountRole;

  private _destroyRef: DestroyRef = inject(DestroyRef);

  public isNavigationOpen: boolean = false;

  public readonly navigationMenu: NavigationDto[] = [
    {
      iconName: 'dashboard',
      routerLink: '/',
      tooltip: 'common.navigation.dashboard.title', // Dashboard',
      isTranslated: true
    },
    {
      iconName: 'water_drop',
      routerLink: '/devices',
      tooltip: 'common.navigation.devices.title',
      isTranslated: true
    },
    {
      iconName: 'notification_sound',
      routerLink: '/alarms',
      tooltip: 'common.navigation.alarms.title',
      isTranslated: true
    },
    {
      iconName: 'map',
      routerLink: '/map',
      tooltip: 'common.navigation.map.title',
      isTranslated: true
    },
    {
      iconName: 'insert_chart',
      routerLink: '/reports',
      tooltip: 'common.navigation.reports.title',
      isTranslated: true
    },
    {
      iconName: 'settings',
      routerLink: '/settings',
      tooltip: 'common.navigation.settings.title',
      needAccess: AccountRole.ADMIN,
      isTranslated: true
    },
    {
      iconName: 'shield_person',
      routerLink: '/super-console',
      exact: false,
      tooltip: 'common.navigation.super-console.title',
      needAccess: AccountRole.SUPER_ADMIN,
      isTranslated: true
    }
  ]

  constructor(
    private readonly _themeService: ThemeService,
    private readonly _authService: AuthService,
    private readonly _logService: LogService,
    private readonly _accountService: AccountService,
    private readonly _webSocketService: WebsocketService,
  ) {
  }

  public ngOnInit(): void {
    this.theme = this._themeService.getCurrentTheme()!;

    this._accountService.account$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(account => {
        this.currentAccount = account as SerializedAccountDto;
      });
  }

  public toggleNavigation(): void {
    this.isNavigationOpen = !this.isNavigationOpen;
  }

  public closNavigation(): void {
    this.isNavigationOpen = false;
  }

  public doLogout(): void {
    this._logService.sendLog({
      description: `Nutzer hat sich ausgeloggt.`,
      target: LoggingTargetEnum.USER,
      action: LoggingActionEnum.LOGOUT,
    });

    this._authService.logout();

    this._webSocketService.disconnect();
  }

  public openEditUserDialog(): void {
    this.isEditDialogOpen = !this.isEditDialogOpen;
  }

  public readonly closeEditUserDialog = (): void => this.openEditUserDialog();

  public toggleTheme(): void {
    this._themeService.toggleTheme();

    this.theme = this._themeService.getCurrentTheme()!;
  }

  public hasAccess(roleToCheck: AccountRole): boolean {
    return this._authService.canAccess(roleToCheck);
  }
}
